// ============================================================================
// TASKDETAIL.JS - VERSIÓN LIMPIA Y MODULAR
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { getTaskRef } from '../utils/taskRef';
import appConfig from '../config/appConfig';
import {
  Edit3,
  Save,
  X,
  Calendar,
  CalendarClock,
  CalendarX,
  RefreshCw,
  Upload,
  Copy,
  Zap,
  Lightbulb,
  ArrowLeft,
  ChevronDown,
  Tag,
  History,
  Layers
} from 'lucide-react';
import { formatEstado } from '../utils/activityText';
import { useTaskStore } from '../store/TaskStore';
import { shallow } from 'zustand/shallow';
import RichTextEditor from './RichTextEditor';
import FileUploader from './FileUploader';
import TaskAttachments from './TaskAttachments';
import { useAuth } from '../auth/AuthContext';
import TaskStatusControl from './TaskStatusControl';
import TaskDocumentationPanel from './TaskDocumentationPanel';
import TaskCommentTimeline from './TaskCommentTimeline';
import { resolveAvatarUrl } from './Avatar';
import AssigneePicker from './AssigneePicker';
import EpicSelector from './EpicSelector'; // 🆕 IMPORTAR EPICSELECTOR
import { getDueDateStatus, getDueDateStatusLabel, formatDueDate } from '../utils/dueDate';
import '../styles/task-detail.css';
import '../styles/task-detail-dos.css';
import '../styles/rich-text-editor.css';
import useToast from '../services/useToast'; // 🆕 TOAST IMPORT
import { highlightCodeBlocks } from '../utils/codeHighlight';
import { useLocation } from 'react-router-dom';
import useMediaQuery from './shared/useMediaQuery';


// En compacto (hasta 767 px) la cabecera de la app es sticky: lo que se fija
// con position: fixed tiene que quedar por debajo de ella, no taparla. En
// escritorio devuelve 0 y el comportamiento es el de siempre.
const getAppHeaderOffset = () => {
  if (typeof window === 'undefined' || !window.matchMedia || !window.matchMedia('(max-width: 767px)').matches) return 0;
  const header = document.querySelector('.header');
  if (!header) return 0;
  const pos = window.getComputedStyle(header).position;
  if (pos !== 'sticky' && pos !== 'fixed') return 0;
  return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
};

function TaskDetail({ task, onBack }) {
  console.log('TaskDetail renderizando con task:', task);

  // Al llegar desde "Añadir bloqueo y comentario" del kebab, el editor de
  // comentario se enfoca automáticamente para explicar el motivo del bloqueo.
  const location = useLocation();
  const focusCommentOnMount = Boolean(location.state?.focusComment);

  // Ficha a una columna (hasta 1024 px, donde colapsa la rejilla) y compacto
  // (hasta 768 inclusive, teléfono y tableta), donde «Detalles» va plegado.
  const isStackedDetail = useMediaQuery('(max-width: 1024px)');
  const isCompactDetail = useMediaQuery('(max-width: 768px)');

  const {
    updateTask,
    reopenTask,
    incrementCounter,
    sprints,
    currentUser,
    isLoading,
    documents,
    fetchDocuments,
    linkDocumentToTask,
    unlinkDocumentFromTask,
    getDocumentsByTask
  } = useTaskStore(
    (s) => ({
      updateTask: s.updateTask,
      reopenTask: s.reopenTask,
      incrementCounter: s.incrementCounter,
      sprints: s.sprints,
      currentUser: s.currentUser,
      isLoading: s.isLoading,
      documents: s.documents,
      fetchDocuments: s.fetchDocuments,
      linkDocumentToTask: s.linkDocumentToTask,
      unlinkDocumentFromTask: s.unlinkDocumentFromTask,
      getDocumentsByTask: s.getDocumentsByTask
    }),
    shallow
  );

  const { isAdmin, user: authUser, token } = useAuth();

  // 🆕 TOAST HOOK
  const { showSuccess, showError, showWarning } = useToast();

  // Estados locales para edición
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(task || {});
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Estados para documentación - solo loading ya que el panel es autónomo
  const [loadingDocs] = useState(false);

  // Estado para controlar si hay archivos adjuntos
  // (La sección de archivos adjuntos es siempre visible; gestiona su propio
  //  estado vacío/carga. Se eliminó el gate hasAttachments, que se auto-ocultaba.)

  // Estados para editores inline
  const [, setIsEditingResponsable] = useState(false);
  const [isSavingResponsable, setIsSavingResponsable] = useState(false);
  const [, setIsEditingEpica] = useState(false);
  const [isSavingEpica, setIsSavingEpica] = useState(false);
  const [, setIsEditingPrioridad] = useState(false);
  const [isSavingPrioridad, setIsSavingPrioridad] = useState(false);

  // Referencias para el formulario
  const nameInputRef = useRef(null);
  const descriptionRef = useRef(null);
  const descriptionSectionRef = useRef(null);
  const descriptionHeaderRef = useRef(null);
  const [descHeaderStuck, setDescHeaderStuck] = useState(false);
  const [descHeaderDimensions, setDescHeaderDimensions] = useState({ width: 0, left: 0, top: 0 });

  // Opciones de campos editables — responsables del tenant (clients.responsables)
  const responsableOptions = [
    { value: '', label: 'Sin asignar' },
    ...appConfig.getResponsables().map(r => ({ value: r, label: r }))
  ];

  const prioridadOptions = [
    { value: 'baja', label: 'Baja' },
    { value: 'media', label: 'Media' },
    { value: 'alta', label: 'Alta' }
  ];

  // ============================================================================
  // 🔧 EFECTOS CORREGIDOS - ELIMINADOS LOS PROBLEMÁTICOS
  // ============================================================================
// Cargar documentos al montar
useEffect(() => {
  if (documents.length === 0) {
    fetchDocuments();
  }
}, [documents.length, fetchDocuments]);
  // 🔧 CORREGIDO: Solo sincronización básica sin fetchTasks
  useEffect(() => {
    if (task && !isUpdating) {
      console.log('TaskDetail: Task prop changed:', task.id);
      
      const validadoPorSEOValue = task.validadoPorSEO !== undefined 
        ? Boolean(task.validadoPorSEO) 
        : false;
      
      setEditedTask({
        ...task,
        validadoPorSEO: validadoPorSEOValue
      });
    }
    // task?.afectacion === undefined en deps: el listado viaja LIGERO y el
    // contenedor rehidrata la tarea completa con el MISMO id; sin este flag,
    // editedTask se quedaba sin historial_estados/historialSprints al entrar
    // desde el panel (Historial y mapa de sprints vacíos).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, isUpdating, task?.afectacion === undefined]); // Solo primitivos

  // Focus en input - SIN PROBLEMAS
  useEffect(() => {
    if (isEditing && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditing]);

  // Aplicar syntax highlighting cuando se muestra la descripción en modo lectura
  useEffect(() => {
    if (!isEditing && descriptionRef.current) {
      highlightCodeBlocks(descriptionRef.current).catch(() => {});
    }
  }, [isEditing, editedTask.descripcion]);

  // Sticky de la cabecera de Descripción durante edición (fixed, igual que la toolbar del editor)
  useEffect(() => {
    if (!isEditing) {
      setDescHeaderStuck(false);
      return;
    }

    const handleScroll = () => {
      const section = descriptionSectionRef.current;
      const header = descriptionHeaderRef.current;
      if (!section || !header) return;

      const sectionRect = section.getBoundingClientRect();
      const headerHeight = header.offsetHeight;
      const offsetTop = getAppHeaderOffset();
      const shouldStick = sectionRect.top < offsetTop && sectionRect.bottom > headerHeight + 120 + offsetTop;

      setDescHeaderStuck(prev => (prev !== shouldStick ? shouldStick : prev));

      if (shouldStick) {
        setDescHeaderDimensions({
          width: sectionRect.width,
          left: sectionRect.left,
          top: offsetTop,
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isEditing]);

  // ============================================================================
  // FUNCIONES DE FORMATEO Y UTILIDADES
  // ============================================================================

  // VALIDACIÓN: Si no hay tarea, mostrar estado de carga/error
  if (!task) {
    console.error('TaskDetail: No task provided');
    return (
      <div className="task-detail-container">
        <div className="task-detail-loading">
          <RefreshCw size={32} className="loading-spinner" />
          <h3>Cargando detalles de la tarea...</h3>
          <p>No se pudo cargar la información de la tarea</p>
          {onBack && (
            <button className="btn-back-to-panel" onClick={onBack}>
              <ArrowLeft size={16} />
              Volver al panel
            </button>
          )}
        </div>
      </div>
    );
  }

  // VALIDACIÓN: Verificar que la tarea tenga las propiedades mínimas
  if (!task.id || !task.nombre) {
    console.error('TaskDetail: Task is missing required properties:', task);
    return (
      <div className="task-detail-container">
        <div className="task-detail-error">
          <h3>Error en la tarea</h3>
          <p>La tarea no tiene la información necesaria para mostrar.</p>
          <pre>{JSON.stringify(task, null, 2)}</pre>
          {onBack && (
            <button className="btn-back-to-panel" onClick={onBack}>
              <ArrowLeft size={16} />
              Volver al panel
            </button>
          )}
        </div>
      </div>
    );
  }

  // ============================================================================
  // FUNCIONES DE MANEJO DE CAMBIOS INLINE - ACTUALIZADAS CON TOAST
  // ============================================================================

  const handleResponsableChange = async (newResponsable) => {
    console.log('Cambiando responsable:', {
      from: editedTask.responsable,
      to: newResponsable,
      taskId: task.id
    });

    setIsSavingResponsable(true);
    const oldResponsable = editedTask.responsable;

    try {
      // Actualización optimista
      setEditedTask(prev => ({
        ...prev,
        responsable: newResponsable
      }));

      const result = await updateTask(task.id, {
        responsable: newResponsable
      },token);

      if (result.success) {
        console.log('Responsable actualizado exitosamente');
        setIsEditingResponsable(false);
        
        // 🔧 ELIMINADO: fetchTasks para evitar bucles
        // Emitir evento personalizado para notificar cambios
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, changes: { responsable: newResponsable } }
        }));
      } else {
        // Revertir si hay error
        console.error('Error al cambiar responsable:', result.error);
        setEditedTask(prev => ({
          ...prev,
          responsable: oldResponsable
        }));
        // 🆕 CAMBIO: alert por showError
        showError('Error al actualizar responsable: ' + result.error);
      }
    } catch (error) {
      // Revertir si hay error
      console.error('Error en handleResponsableChange:', error);
      setEditedTask(prev => ({
        ...prev,
        responsable: oldResponsable
      }));
      // 🆕 CAMBIO: alert por showError
      showError('Error al actualizar responsable: ' + error.message);
    } finally {
      setIsSavingResponsable(false);
    }
  };

  // Persona asignada (assigned_to) — actualización inline optimista.
  const handleAssignedChange = async (uid) => {
    const old = editedTask.assigned_to;
    setEditedTask(prev => ({ ...prev, assigned_to: uid }));
    try {
      const result = await updateTask(task.id, { assigned_to: uid }, token);
      if (result.success) {
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, changes: { assigned_to: uid } }
        }));
      } else {
        setEditedTask(prev => ({ ...prev, assigned_to: old }));
        showError('Error al asignar persona: ' + result.error);
      }
    } catch (error) {
      setEditedTask(prev => ({ ...prev, assigned_to: old }));
      showError('Error al asignar persona: ' + error.message);
    }
  };

  // Fecha de vencimiento — actualización inline optimista. Un string vacío
  // desasigna la fecha (se envía null al backend, que valida el formato).
  const handleFechaVencimientoChange = async (value) => {
    const nueva = value || null;
    const old = editedTask.fecha_vencimiento ?? null;
    if (nueva === old) return;
    setEditedTask(prev => ({ ...prev, fecha_vencimiento: nueva }));
    try {
      const result = await updateTask(task.id, { fecha_vencimiento: nueva }, token);
      if (result.success) {
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, changes: { fecha_vencimiento: nueva } }
        }));
      } else {
        setEditedTask(prev => ({ ...prev, fecha_vencimiento: old }));
        showError('Error al actualizar la fecha de vencimiento: ' + result.error);
      }
    } catch (error) {
      setEditedTask(prev => ({ ...prev, fecha_vencimiento: old }));
      showError('Error al actualizar la fecha de vencimiento: ' + error.message);
    }
  };

  const handleEpicaChange = async (newEpica) => {
    console.log('Cambiando épica:', {
      from: editedTask.epica,
      to: newEpica,
      taskId: task.id
    });

    setIsSavingEpica(true);
    const oldEpica = editedTask.epica;

    try {
      // Actualización optimista
      setEditedTask(prev => ({
        ...prev,
        epica: newEpica
      }));

      const result = await updateTask(task.id, {
        epica: newEpica
      },token);

      if (result.success) {
        console.log('Épica actualizada exitosamente');
        setIsEditingEpica(false);
        
        // 🔧 ELIMINADO: fetchTasks para evitar bucles
        // Emitir evento personalizado para notificar cambios
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, changes: { epica: newEpica } }
        }));
      } else {
        // Revertir si hay error
        console.error('Error al cambiar épica:', result.error);
        setEditedTask(prev => ({
          ...prev,
          epica: oldEpica
        }));
        // 🆕 CAMBIO: alert por showError
        showError('Error al actualizar épica: ' + result.error);
      }
    } catch (error) {
      // Revertir si hay error
      console.error('Error en handleEpicaChange:', error);
      setEditedTask(prev => ({
        ...prev,
        epica: oldEpica
      }));
      // 🆕 CAMBIO: alert por showError
      showError('Error al actualizar épica: ' + error.message);
    } finally {
      setIsSavingEpica(false);
    }
  };

  const handlePrioridadChange = async (newPrioridad) => {
    console.log('Cambiando prioridad:', {
      from: editedTask.prioridad,
      to: newPrioridad,
      taskId: task.id
    });

    setIsSavingPrioridad(true);
    const oldPrioridad = editedTask.prioridad;

    try {
      // Actualización optimista
      setEditedTask(prev => ({
        ...prev,
        prioridad: newPrioridad
      }));

      const result = await updateTask(task.id, {
        prioridad: newPrioridad
      },token);

      if (result.success) {
        console.log('Prioridad actualizada exitosamente');
        setIsEditingPrioridad(false);
        
        // 🔧 ELIMINADO: fetchTasks para evitar bucles
        // Emitir evento personalizado para notificar cambios
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, changes: { prioridad: newPrioridad } }
        }));
      } else {
        // Revertir si hay error
        console.error('Error al cambiar prioridad:', result.error);
        setEditedTask(prev => ({
          ...prev,
          prioridad: oldPrioridad
        }));
        // 🆕 CAMBIO: alert por showError
        showError('Error al actualizar prioridad: ' + result.error);
      }
    } catch (error) {
      // Revertir si hay error
      console.error('Error en handlePrioridadChange:', error);
      setEditedTask(prev => ({
        ...prev,
        prioridad: oldPrioridad
      }));
      // 🆕 CAMBIO: alert por showError
      showError('Error al actualizar prioridad: ' + error.message);
    } finally {
      setIsSavingPrioridad(false);
    }
  };

  // Funciones de formateo
  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  // ============================================================================
  // FUNCIONES DE MANEJO PRINCIPAL - ACTUALIZADAS CON TOAST
  // ============================================================================

  // Manejar guardar cambios
  const handleSave = async () => {
    if (!editedTask.nombre?.trim()) {
      // 🆕 CAMBIO: alert por showWarning
      showWarning('El nombre de la tarea es obligatorio');
      return;
    }

    try {
      const result = await updateTask(task.id, editedTask, token);
      if (result.success) {
        setIsEditing(false);
        console.log('Task updated successfully');
        
        // 🔧 ELIMINADO: fetchTasks para evitar bucles
        // Emitir evento personalizado para notificar cambios
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, changes: editedTask }
        }));
      } else {
        // 🆕 CAMBIO: alert por showError
        showError('Error al actualizar la tarea: ' + result.error);
      }
    } catch (error) {
      // 🆕 CAMBIO: alert por showError
      showError('Error al actualizar la tarea');
    }
  };

  // Manejar cambio de estado - ACTUALIZADO CON TOAST
  const handleStatusChange = async (newStatus) => {
    console.log(`Cambiando estado de ${editedTask.estado} a ${newStatus}`);
    
    const oldStatus = editedTask.estado;
    
    try {
      setIsUpdating(true); 
      // ACTUALIZACIÓN OPTIMISTA - cambiar interfaz inmediatamente
      setEditedTask(prev => ({
        ...prev,
        estado: newStatus,
        fechaModificacion: new Date().toISOString()
      }));
      
      const updatedTaskData = {
        ...editedTask,
        estado: newStatus,
        fechaModificacion: new Date().toISOString()
      };
      
      const result = await updateTask(task.id, updatedTaskData, token);
      
      if (result && result.success) {
        console.log(`Estado actualizado exitosamente a: ${newStatus}`);
        
        // 🔧 ELIMINADO: fetchTasks para evitar bucles
        // Emitir evento personalizado para notificar cambios
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, newStatus }
        }));
        
      } else {
        // Revertir si hay error
        console.error('Error al cambiar estado:', result?.error);
        setEditedTask(prev => ({
          ...prev,
          estado: oldStatus // Revertir
        }));
        // 🆕 CAMBIO: alert por showError
        showError('Error al cambiar el estado: ' + (result?.error || 'Error desconocido'));
      }
    } catch (error) {
      // Revertir si hay error
      console.error('Error en handleStatusChange:', error);
      setEditedTask(prev => ({
        ...prev,
        estado: oldStatus // Revertir
      }));
      // 🆕 CAMBIO: alert por showError
      showError('Error al cambiar estado: ' + error.message);
    } finally {
      setIsUpdating(false); // Permitir actualizaciones externas
    }
  };

  // Manejar toggle de validación SEO - ACTUALIZADO CON TOAST
  const handleToggleValidacionSEO = async () => {
    // CORRECCIÓN: Manejar correctamente valores undefined
    const currentValue = editedTask.validadoPorSEO !== undefined 
      ? Boolean(editedTask.validadoPorSEO) 
      : false;
    const newValue = !currentValue;
    
    console.log(`Cambiando validación SEO:`, {
      editedTaskValue: editedTask.validadoPorSEO,
      currentValue,
      newValue,
      taskId: task.id
    });
    
    try {
      // ACTUALIZACIÓN OPTIMISTA - cambiar interfaz inmediatamente
      setEditedTask(prev => ({
        ...prev,
        validadoPorSEO: newValue,
        fechaModificacion: new Date().toISOString()
      }));
      
      const updatedTaskData = {
        ...editedTask,
        validadoPorSEO: newValue,
        fechaModificacion: new Date().toISOString()
      };
      
      const result = await updateTask(task.id, updatedTaskData, token);
      
      if (result && result.success) {
        console.log(`Validación SEO actualizada exitosamente:`, {
          newValue,
          response: result
        });
        
        // FORZAR actualización inmediata del estado local con la respuesta del servidor
        if (result.data && typeof result.data.validadoPorSEO !== 'undefined') {
          setEditedTask(prev => ({
            ...prev,
            ...result.data,
            validadoPorSEO: Boolean(result.data.validadoPorSEO)
          }));
        }
        
        // 🔧 ELIMINADO: fetchTasks para evitar bucles
        // Emitir evento personalizado para notificar cambios
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, validadoPorSEO: newValue }
        }));
        
      } else {
        // Revertir si hay error
        console.error('Error al cambiar validación SEO:', result?.error);
        setEditedTask(prev => ({
          ...prev,
          validadoPorSEO: currentValue // Revertir
        }));
        // 🆕 CAMBIO: alert por showError
        showError('Error al actualizar validación SEO: ' + (result?.error || 'Error desconocido'));
      }
    } catch (error) {
      // Revertir si hay error
      console.error('Error en handleToggleValidacionSEO:', error);
      setEditedTask(prev => ({
        ...prev,
        validadoPorSEO: currentValue // Revertir
      }));
      // 🆕 CAMBIO: alert por showError
      showError('Error al actualizar validación SEO: ' + error.message);
    }
  };

  // Reabrir tarea - ACTUALIZADO CON TOAST
  const handleReopenTask = async () => {
    console.log('Reabriendo tarea...');
    
    const oldStatus = editedTask.estado;
    const oldVecesRealizada = editedTask.vecesRealizada || editedTask.veces_completada || 0;
    
    try {
      // ACTUALIZACIÓN OPTIMISTA - incrementar contador al reabrir
      const newCount = oldVecesRealizada + 1;
      setEditedTask(prev => ({
        ...prev,
        estado: 'pendiente',
        fechaModificacion: new Date().toISOString(),
        // Actualizar todos los campos de contador
        vecesRealizada: newCount,
        veces_completada: newCount,
        vecesCompletada: newCount
      }));
      
      // 1. REABRIR la tarea
      const reopenResult = await reopenTask(task.id, token);
      
      if (reopenResult && reopenResult.success) {
        console.log('Tarea reabierta exitosamente, ahora incrementando contador...');
        
        // 2. INCREMENTAR el contador manualmente
        const counterResult = await incrementCounter(task.id, token);
        
        if (counterResult && counterResult.success) {
          console.log('Contador incrementado exitosamente:', counterResult.data);
          
          // Actualizar con los datos reales del servidor
          setEditedTask(prev => ({
            ...prev,
            vecesRealizada: counterResult.data.veces_completada,
            veces_completada: counterResult.data.veces_completada,
            vecesCompletada: counterResult.data.veces_completada
          }));
          
          showSuccess('Tarea reabierta y contador incrementado');
        } else {
          console.warn('Tarea reabierta pero no se pudo incrementar contador:', counterResult?.error);
          showWarning('Tarea reabierta pero no se actualizó el contador');
        }
        
        // Emitir evento personalizado para notificar cambios
        window.dispatchEvent(new CustomEvent('taskUpdated', {
          detail: { taskId: task.id, newStatus: 'pendiente', counterIncremented: true }
        }));
        
        // Volver a la lista después de un breve delay
        setTimeout(() => {
          if (onBack) {
            onBack();
          }
        }, 1500);
      } else {
        // Revertir si hay error en reopen
        console.error('Error al reabrir:', reopenResult?.error);
        setEditedTask(prev => ({
          ...prev,
          estado: oldStatus,
          vecesRealizada: oldVecesRealizada
        }));
        const errorMsg = reopenResult?.error || reopenResult?.message || 'Error desconocido al reabrir';
        showError('Error al reabrir la tarea: ' + errorMsg);
      }
    } catch (error) {
      // Revertir si hay error
      console.error('Error en handleReopenTask:', error);
      setEditedTask(prev => ({
        ...prev,
        estado: oldStatus,
        vecesRealizada: oldVecesRealizada
      }));
      showError('Error al reabrir la tarea: ' + error.message);
    }
  };

  // Obtener badge de prioridad
  const getPriorityBadge = (prioridad) => {
    return (
      <span className={`task-priority-badge task-priority-${prioridad || 'media'}`}>
        {prioridad || 'media'}
      </span>
    );
  };

  console.log('TaskDetail: Renderizando tarea completa:', {
    id: editedTask.id,
    nombre: editedTask.nombre,
    estado: editedTask.estado,
    validadoPorSEO: editedTask.validadoPorSEO
  });

  // Lista de detalles (sprint, autor, asignación, fechas): se pinta igual en
  // escritorio y dentro del plegable de la ficha a una columna.
  // Pista del plegable: persona asignada y vencimiento, para no esconder sin
  // aviso los datos que se miran a diario cuando Detalles va plegado.
  const detailsHint = [
    editedTask.asignado_nombre || null,
    editedTask.fecha_vencimiento ? `vence ${formatDueDate(editedTask.fecha_vencimiento)}` : null,
  ].filter(Boolean).join(' · ');

  const detailsList = (
    <div className="details-list">
      {(() => {
        const sprintId = editedTask.sprintActual || editedTask.sprint_actual;
        const taskSprint = sprintId ? sprints?.find(s => s.id === sprintId) : null;
        return (
          <>
            <div className="detail-row">
              <span className="detail-label">Sprint</span>
              <span className="detail-value">
                <span className={`detail-sprint-dot${taskSprint ? '' : ' is-backlog'}`}></span>
                {taskSprint ? taskSprint.nombre : 'Backlog'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Creada por</span>
              <span className="detail-value">
                {(() => {
                  const name = editedTask.creador_nombre || editedTask.creadorNombre || 'Usuario desconocido';
                  const initials = name.split(' ').map(p => p.charAt(0)).slice(0, 2).join('').toUpperCase() || 'U';
                  const avatar = editedTask.creador_avatar || editedTask.creadorAvatar;
                  return (
                    <>
                      <span className={`detail-avatar ${avatar ? 'detail-avatar-photo' : ''}`}>
                        {initials}
                        {avatar && (
                          <img
                            src={resolveAvatarUrl(avatar)}
                            alt={name}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                      </span>
                      {name}
                    </>
                  );
                })()}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Persona asignada</span>
              <span className="detail-value detail-value-assignee">
                <AssigneePicker
                  value={editedTask.assigned_to}
                  assigneeName={editedTask.asignado_nombre}
                  assigneeAvatar={editedTask.asignado_avatar}
                  size={22}
                  align="left"
                  showName={true}
                  onChange={handleAssignedChange}
                />
              </span>
            </div>
            {(() => {
              const dueValue = (editedTask.fecha_vencimiento || '').slice(0, 10);
              const dueStatus = getDueDateStatus(editedTask.fecha_vencimiento, editedTask.estado);
              const statusLabel = getDueDateStatusLabel(dueStatus);
              const DueIcon = dueStatus === 'overdue'
                ? CalendarX
                : dueStatus === 'today'
                  ? CalendarClock
                  : Calendar;
              return (
                <div className="detail-row">
                  <span className="detail-label">Fecha de vencimiento</span>
                  <span className="detail-value detail-value-duedate">
                    <span className={`detail-duedate-control is-${dueStatus}`}>
                      <DueIcon size={13} />
                      <input
                        type="date"
                        className="detail-duedate-input"
                        value={dueValue}
                        onChange={(e) => handleFechaVencimientoChange(e.target.value)}
                        aria-label="Fecha de vencimiento"
                      />
                      {dueValue && (
                        <button
                          type="button"
                          className="detail-duedate-clear"
                          title="Quitar fecha de vencimiento"
                          onClick={() => handleFechaVencimientoChange('')}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </span>
                    {statusLabel && (
                      <span className={`detail-duedate-badge is-${dueStatus}`}>
                        {statusLabel}
                      </span>
                    )}
                  </span>
                </div>
              );
            })()}
            <div className="detail-row">
              <span className="detail-label">Fin del sprint</span>
              <span className="detail-value detail-value-date">
                <Calendar size={13} />
                {taskSprint?.fecha_fin ? formatDate(taskSprint.fecha_fin) : '—'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Creada el</span>
              <span className="detail-value detail-value-date">
                <Calendar size={13} />
                {(() => {
                  const created = editedTask.fecha_creacion
                    || editedTask.fechaCreacion
                    || editedTask.created_at
                    || editedTask.createdAt;
                  return created ? formatDate(created) : '—';
                })()}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Modificada</span>
              <span className="detail-value detail-value-date">
                <Calendar size={13} />
                {(() => {
                  const updated = editedTask.fecha_actualizacion
                    || editedTask.fechaActualizacion
                    || editedTask.fechaModificacion
                    || editedTask.updated_at;
                  return updated ? formatDate(updated) : '—';
                })()}
              </span>
            </div>
          </>
        );
      })()}
    </div>
  );

  return (
    <div className="task-detail-container">
      
      {/* Botón de navegación superior */}
      <div className="task-detail-navigation">
        <button
          className="doc-back"
          onClick={onBack}
        >
          <ArrowLeft size={14} />
          Volver al panel
        </button>
      </div>

      <div className="task-detail-content">

        {/* Cuerpo principal */}
        <div className="task-detail-body">

          {/* ÁREA PRINCIPAL (CENTRO) */}
          <div className="task-main-content">

            {/* Header de la tarea */}
            <div className="task-detail-header">
              <div className="task-header-content">

            {/* Línea superior: TASK-XX · Sprint */}
            <div className="task-detail-meta-top">
              <span className="task-id-label">{getTaskRef(editedTask)}</span>
              {(() => {
                const sprintId = editedTask.sprintActual || editedTask.sprint_actual;
                const taskSprint = sprintId ? sprints?.find(s => s.id === sprintId) : null;
                return (
                  <>
                    <span className="task-meta-dot">·</span>
                    <span className={`task-sprint-pill${taskSprint ? '' : ' is-backlog'}`}>
                      <span className="sprint-pill-dot"></span>
                      {taskSprint ? `Sprint ${taskSprint.nombre}` : 'Backlog'}
                    </span>
                  </>
                );
              })()}
            </div>

            {/* Título */}
            <div className="task-detail-title-section">
              <div className="task-title-wrapper">
                {isEditing ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={editedTask.nombre || ''}
                    onChange={(e) => { const nombre = e.target.value; setEditedTask(prev => ({ ...prev, nombre })); }}
                    className="task-title-edit"
                    placeholder="Nombre de la tarea"
                  />
                ) : (
                  <h1 className="task-detail-title">{editedTask.nombre || 'Tarea sin nombre'}</h1>
                )}
              </div>

            </div>

            {/* Metadata de la tarea */}
            <div className="task-metadata">
              {/* ÉPICA EDITABLE CON EPICSELECTOR */}
              <div className="metadata-item metadata-item-epic">
                <span className="metadata-label">Épica</span>
                {isSavingEpica ? (
                  <span className="saving-indicator">
                    <RefreshCw size={14} className="spinning" />
                    Guardando...
                  </span>
                ) : (
                  <div className="metadata-epic-selector">
                    <EpicSelector
                      value={editedTask.epica || ''}
                      onChange={(value) => handleEpicaChange(value)}
                      disabled={isSavingEpica}
                      placeholder="Seleccionar épica..."
                    />
                  </div>
                )}
              </div>

              {/* RESPONSABLE EDITABLE INLINE */}
              <div className="metadata-item metadata-item-select">
                <span className="metadata-label">Responsable</span>
                {isSavingResponsable ? (
                  <span className="saving-indicator">
                    <RefreshCw size={14} className="spinning" />
                  </span>
                ) : (
                  <span className="metadata-native-wrapper">
                    <span className="metadata-native-display">
                      <span className="metadata-native-text" title={editedTask.responsable || 'Sin asignar'}>
                        {editedTask.responsable || 'Sin asignar'}
                      </span>
                      <ChevronDown size={12} className="metadata-native-chevron" />
                    </span>
                    <select
                      className="metadata-native-select"
                      value={editedTask.responsable || ''}
                      onChange={(e) => handleResponsableChange(e.target.value)}
                      disabled={isSavingResponsable}
                    >
                      {responsableOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </span>
                )}
              </div>

              {/* PRIORIDAD EDITABLE INLINE */}
              <div className="metadata-item metadata-item-select">
                <span className="metadata-label">Prioridad</span>
                {isSavingPrioridad ? (
                  <span className="saving-indicator">
                    <RefreshCw size={14} className="spinning" />
                  </span>
                ) : (
                  <span className="metadata-native-wrapper">
                    <span className="metadata-native-display">
                      {getPriorityBadge(editedTask.prioridad)}
                      <ChevronDown size={12} className="metadata-native-chevron" />
                    </span>
                    <select
                      className="metadata-native-select"
                      value={editedTask.prioridad || 'media'}
                      onChange={(e) => handlePrioridadChange(e.target.value)}
                      disabled={isSavingPrioridad}
                    >
                      {prioridadOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </span>
                )}
              </div>
              <div className="metadata-item">
                <RefreshCw className="metadata-icon" />
                <span className="metadata-label">Reps:</span>
                <span className="metadata-value">{editedTask.vecesRealizada || 0}</span>
              </div>
            </div>
          </div>
        </div>

            {/* 1. DESCRIPCIÓN EXPANDIDA */}
            <div className="task-description-section expanded" ref={descriptionSectionRef}>
              <div
                className={`description-header ${descHeaderStuck ? 'description-header-stuck' : ''}`}
                ref={descriptionHeaderRef}
                style={descHeaderStuck ? {
                  position: 'fixed',
                  top: descHeaderDimensions.top || 0,
                  left: descHeaderDimensions.left,
                  width: descHeaderDimensions.width,
                  zIndex: 1100,
                } : undefined}
              >
                <h2 className="section-title description-title">
                  <Lightbulb className="section-icon" />
                  Descripción
                </h2>
                {isEditing ? (
                  <div className="description-header-actions">
                    <button
                      type="button"
                      className="description-edit-btn description-save-btn"
                      onClick={handleSave}
                      disabled={isLoading}
                    >
                      <Save size={14} />
                      Guardar
                    </button>
                    <button
                      type="button"
                      className="description-edit-btn description-cancel-btn"
                      onClick={() => {
                        setIsEditing(false);
                        setEditedTask(task);
                      }}
                    >
                      <X size={14} />
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="description-edit-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit3 size={14} />
                    Editar
                  </button>
                )}
              </div>
              {descHeaderStuck && (
                <div
                  aria-hidden="true"
                  style={{ height: descriptionHeaderRef.current?.offsetHeight || 0 }}
                />
              )}

              {isEditing ? (
                <RichTextEditor
                  value={editedTask.descripcion || ''}
                  onChange={(value) => setEditedTask(prev => ({ ...prev, descripcion: value }))}
                  placeholder="Describe detalladamente la tarea..."
                  height="480px"
                  taskId={task.id}
                />
              ) : (
                <div className="task-description expanded">
                  <div
                    ref={descriptionRef}
                    className="rich-text-preview"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(editedTask.descripcion || '<p>Sin descripción disponible</p>') }}
                  />
                </div>
              )}
            </div>

            {/* 2. ARCHIVOS ADJUNTOS (siempre visible; gestiona su propio estado vacío) */}
            <TaskAttachments taskId={editedTask.id} />

            {/* 3. COMENTARIOS AVANZADOS */}
            <div className="task-comments-section">
              <TaskCommentTimeline
                task={editedTask}
                currentUser={currentUser || authUser}
                focusOnMount={focusCommentOnMount}
                onCommentChange={(action, comment) => {
                  console.log('Comment action:', action, comment);
                  // Opcional: actualizar contadores o UI
                }}
              />
            </div>
          </div>

          {/* SIDEBAR (COLUMNA DERECHA) */}
          <div className="task-sidebar">
            
            {/* 1. CONTROL DE ESTADO MODULARIZADO */}
            <TaskStatusControl
              task={editedTask}
              isLoading={isLoading || isUpdating}
              isAdmin={isAdmin()}
              onStatusChange={handleStatusChange}
              onToggleSEO={handleToggleValidacionSEO}
              onReopen={handleReopenTask}
            />

            {/* 2. DETALLES: en la ficha a una columna (hasta 1024 px) va plegable,
                plegado en teléfono y tableta (hasta 768) y abierto entre 769 y 1024.
                En escritorio, igual que siempre. */}
            {isStackedDetail ? (
              <details
                key={isCompactDetail ? 'compact' : 'wide'}
                className="task-details-panel task-details-collapsible"
                open={!isCompactDetail}
              >
                <summary className="task-details-summary">
                  <span className="task-details-summary-text">
                    <span className="info-panel-title">
                      <Tag className="section-icon" />
                      Detalles de la tarea
                    </span>
                    {detailsHint && (
                      <span className="task-details-summary-hint">{detailsHint}</span>
                    )}
                  </span>
                  <ChevronDown size={16} className="task-details-summary-chevron" aria-hidden="true" />
                </summary>
                {detailsList}
              </details>
            ) : (
              <div className="task-details-panel">
                <div className="info-panel-header">
                  <h3 className="info-panel-title">
                    <Tag className="section-icon" />
                    Detalles
                  </h3>
                </div>
                {detailsList}
              </div>
            )}

            {/* 3. DOCUMENTACIÓN VINCULADA MODULARIZADA */}
            <TaskDocumentationPanel
              taskId={task.id}
              documents={documents}
              linkDocumentToTask={linkDocumentToTask}
              unlinkDocumentFromTask={unlinkDocumentFromTask}
              getDocumentsByTask={getDocumentsByTask}
              currentUser={currentUser}
              authUser={authUser}
              loadingDocs={loadingDocs}
              token={token}
            />

            {/* 3. ACCIONES RÁPIDAS */}
            <div className="task-actions-panel">
              <div className="info-panel-header">
                <h3 className="info-panel-title">
                  <Zap className="section-icon" />
                  Acciones rápidas
                </h3>
              </div>
              <div className="actions-panel-content">
                
                <button 
                  className="action-button"
                  onClick={() => setShowFileUploader(true)}
                >
                  <Upload className="action-button-icon" />
                  Subir archivos
                </button>

                <button
                  className="action-button"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    // 🆕 CAMBIO: alert por showSuccess
                    showSuccess('Enlace copiado al portapapeles');
                  }}
                >
                  <Copy className="action-button-icon" />
                  Copiar enlace
                </button>
              </div>
            </div>

            {/* 4. HISTORIAL DE ESTADOS */}
            {(() => {
              const historial = editedTask.historial_estados || editedTask.historialEstados || [];
              if (!Array.isArray(historial) || historial.length === 0) return null;
              const stateLabels = {
                'pendiente': 'Pendiente',
                'en-curso': 'En curso',
                'completada': 'Completada',
                'reabierta': 'Reabierta'
              };
              const stateColors = {
                'pendiente': '#f59e0b',
                'en-curso': '#2563eb',
                'completada': '#16a34a',
                'reabierta': '#dc2626'
              };
              const ordered = [...historial].reverse();
              return (
                <div className="task-history-sidebar-panel">
                  <div className="info-panel-header">
                    <h3 className="info-panel-title">
                      <History className="section-icon" />
                      Historial
                    </h3>
                  </div>
                  <ul className="history-timeline-list">
                    {ordered.map((entry, idx) => (
                      <li key={idx} className="history-entry">
                        <span
                          className="history-entry-dot"
                          style={{ background: stateColors[entry.estado] || '#94a3b8' }}
                        />
                        <div className="history-entry-content">
                          <div className="history-entry-line">
                            <span className="history-entry-state">
                              {stateLabels[entry.estado] || entry.estado}
                            </span>
                            <span className="history-entry-date">{formatDate(entry.fecha)}</span>
                          </div>
                          <div className="history-entry-user">{entry.usuario_nombre || 'Usuario desconocido'}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* 5. SPRINTS POR LOS QUE HA PASADO (último módulo del sidebar) */}
            {(() => {
              const historial = editedTask.historialSprints || editedTask.historial_sprints || [];
              const currentSprintId = editedTask.sprintActual || editedTask.sprint_actual || null;

              // Pasos: primero el histórico registrado (sprints por los que ya
              // pasó la tarea). El sprint ACTUAL solo entra en historial_sprints
              // al cerrarse, así que si no está, lo añadimos al final como "actual".
              const steps = (Array.isArray(historial) ? historial : []).map((h) => ({
                id: h.sprint_id,
                nombre:
                  h.sprint_nombre ||
                  (h.sprint_id ? sprints?.find((s) => s.id === h.sprint_id)?.nombre : null) ||
                  'Sprint',
                estadoFinal: h.estado_final,
                isCurrent: Boolean(currentSprintId && h.sprint_id === currentSprintId),
              }));

              if (currentSprintId && !steps.some((s) => s.id === currentSprintId)) {
                const cur = sprints?.find((s) => s.id === currentSprintId);
                steps.push({
                  id: currentSprintId,
                  nombre: cur?.nombre || 'Sprint actual',
                  estadoFinal: null,
                  isCurrent: true,
                });
              }

              if (steps.length === 0) return null;

              return (
                <div className="task-sprints-passed-panel">
                  <div className="info-panel-header">
                    <h3 className="info-panel-title">
                      <Layers className="section-icon" />
                      Sprints por los que ha pasado
                    </h3>
                  </div>
                  <ul className="sprint-steps-list">
                    {steps.map((step, idx) => {
                      const isDone = step.estadoFinal === 'completada';
                      const dotClass = step.isCurrent
                        ? 'is-current'
                        : isDone
                          ? 'is-done'
                          : 'is-past';
                      return (
                        <li className="sprint-step" key={`${step.id}-${idx}`}>
                          <span className={`sprint-step-dot ${dotClass}`}></span>
                          <div className="sprint-step-body">
                            <div className="sprint-step-name">{step.nombre}</div>
                            <div className="sprint-step-status">
                              {step.isCurrent ? (
                                <>
                                  <span className="sprint-step-badge is-current">
                                    {formatEstado(editedTask.estado)}
                                  </span>
                                  <span className="sprint-step-actual">· actual</span>
                                </>
                              ) : (
                                <span
                                  className={`sprint-step-badge ${isDone ? 'is-done' : 'is-past'}`}
                                >
                                  {isDone ? 'Terminada' : 'Sin terminar'}
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Modal de subida de archivos */}
      {showFileUploader && (
        <FileUploader
          isOpen={showFileUploader}
          onClose={() => setShowFileUploader(false)}
          taskId={task.id} 
          onUploadSuccess={(files) => {
            console.log('Files uploaded successfully:', files);
            // Forzar actualización del componente TaskAttachments
            window.dispatchEvent(new CustomEvent('filesUploaded', { 
              detail: { taskId: task.id, files } 
            }));
          }}
        />
      )}
    </div>
  );
}

export default TaskDetail;